import React, { useEffect, useMemo, useRef } from "react";
import {
  buildDualMontageUniquenessMap,
  deriveDualMontageLanes,
  dualMontagePairLabels,
  type DualMontageChannelSeries,
  type DualMontageDerivedLane,
  type DualMontagePair,
} from "../utils/dualMontageReviewUtils";
import "./DualMontageReviewPanel.css";

export type DualMontageReviewPanelProps = {
  leftMontageName: string;
  rightMontageName: string;
  leftPairs: DualMontagePair[];
  rightPairs: DualMontagePair[];
  channels: DualMontageChannelSeries[];
  currentTime: number;
  duration: number;
  syncPlayback: boolean;
  syncCursor: boolean;
  syncZoom: boolean;
  diffHighlight: boolean;
  /** Shared page width in seconds when zoom is synced (falls back to 10). */
  timeScaleSeconds?: number;
  onSeek?: (timeSeconds: number) => void;
};

function resolvePaperTheme(element: HTMLElement | null): "light" | "dark" | "high_contrast" {
  const viewer = element?.closest(".eeg-viewer") as HTMLElement | null;
  const theme =
    viewer?.getAttribute("data-eeg-paper-theme") ||
    document.documentElement.getAttribute("data-eeg-workspace-theme") ||
    "dark";
  if (theme === "light" || theme === "high_contrast") return theme;
  return "dark";
}

function drawPane(params: {
  canvas: HTMLCanvasElement;
  lanes: DualMontageDerivedLane[];
  currentTime: number;
  duration: number;
  timeScaleSeconds: number;
  diffHighlight: boolean;
  emptyMessage: string;
}) {
  const {
    canvas,
    lanes,
    currentTime,
    duration,
    timeScaleSeconds,
    diffHighlight,
    emptyMessage,
  } = params;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const width = canvas.clientWidth || 640;
  const height = canvas.clientHeight || 112;
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;

  const theme = resolvePaperTheme(canvas);
  const background =
    theme === "light" ? "#ffffff" : theme === "high_contrast" ? "#000000" : "#0b1220";
  const labelColor =
    theme === "light" ? "#334155" : theme === "high_contrast" ? "#ffffff" : "#cbd5e1";
  const emptyColor = theme === "light" ? "#64748b" : "#94a3b8";
  const playhead = theme === "light" ? "#d97706" : "#fbbf24";

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  if (lanes.length === 0) {
    ctx.fillStyle = emptyColor;
    ctx.font = "13px sans-serif";
    ctx.fillText(emptyMessage, 16, 28);
    return;
  }

  const visible = lanes.slice(0, 16);
  const laneHeight = height / visible.length;
  const safeDuration = Math.max(duration, 0.001);
  const windowSeconds = Math.max(timeScaleSeconds, 0.5);
  const windowStart = Math.max(
    0,
    Math.min(currentTime - windowSeconds * 0.2, Math.max(0, safeDuration - windowSeconds)),
  );
  const windowEnd = Math.min(safeDuration, windowStart + windowSeconds);

  visible.forEach((lane, index) => {
    const midY = laneHeight * index + laneHeight / 2;
    const uniqueness = lane.uniqueness;
    let stroke = `hsl(${(index * 37) % 360} 70% ${theme === "light" ? "40%" : "68%"})`;
    if (diffHighlight && uniqueness === "left-only") stroke = "#2563eb";
    if (diffHighlight && uniqueness === "right-only") stroke = "#dc2626";

    ctx.beginPath();
    ctx.strokeStyle = stroke;
    ctx.lineWidth = diffHighlight && uniqueness !== "shared" ? 1.6 : 1;

    const sampleCount = lane.samples.length;
    if (sampleCount < 2) {
      ctx.moveTo(0, midY);
      ctx.lineTo(width, midY);
    } else {
      const totalDuration = safeDuration;
      for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
        const t = (sampleIndex / Math.max(sampleCount - 1, 1)) * totalDuration;
        if (t < windowStart || t > windowEnd) continue;
        const x = ((t - windowStart) / windowSeconds) * width;
        const y = midY - lane.samples[sampleIndex] * 0.008;
        if (sampleIndex === 0 || t === windowStart) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
    }
    ctx.stroke();

    ctx.fillStyle =
      diffHighlight && uniqueness !== "shared"
        ? uniqueness === "left-only"
          ? "#1d4ed8"
          : "#b91c1c"
        : labelColor;
    ctx.font = "11px sans-serif";
    const badge =
      diffHighlight && uniqueness !== "shared"
        ? uniqueness === "left-only"
          ? "L· "
          : "R· "
        : "";
    ctx.fillText(`${badge}${lane.label}`, 8, laneHeight * index + 14);
  });

  const playheadX = ((currentTime - windowStart) / windowSeconds) * width;
  ctx.strokeStyle = playhead;
  ctx.beginPath();
  ctx.moveTo(playheadX, 0);
  ctx.lineTo(playheadX, height);
  ctx.stroke();
}

function DualMontagePane({
  title,
  lanes,
  currentTime,
  duration,
  timeScaleSeconds,
  diffHighlight,
  emptyMessage,
  onSeek,
}: {
  title: string;
  lanes: DualMontageDerivedLane[];
  currentTime: number;
  duration: number;
  timeScaleSeconds: number;
  diffHighlight: boolean;
  emptyMessage: string;
  onSeek?: (timeSeconds: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawPane({
      canvas,
      lanes,
      currentTime,
      duration,
      timeScaleSeconds,
      diffHighlight,
      emptyMessage,
    });
  }, [
    lanes,
    currentTime,
    duration,
    timeScaleSeconds,
    diffHighlight,
    emptyMessage,
  ]);

  return (
    <div className="dual-montage-review__pane">
      <div className="dual-montage-review__pane-header">
        <strong>{title}</strong>
        <span className="dual-montage-review__pane-meta">
          {lanes.length} channel{lanes.length === 1 ? "" : "s"}
        </span>
      </div>
      <canvas
        ref={canvasRef}
        className="dual-montage-review__canvas"
        aria-label={`${title} dual montage waveform`}
        onClick={(event) => {
          if (!onSeek) return;
          const rect = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - rect.left) / Math.max(rect.width, 1);
          const windowSeconds = Math.max(timeScaleSeconds, 0.5);
          const windowStart = Math.max(
            0,
            Math.min(
              currentTime - windowSeconds * 0.2,
              Math.max(0, duration - windowSeconds),
            ),
          );
          onSeek(
            Math.max(
              0,
              Math.min(duration, windowStart + ratio * windowSeconds),
            ),
          );
        }}
      />
    </div>
  );
}

export const DualMontageReviewPanel: React.FC<DualMontageReviewPanelProps> = ({
  leftMontageName,
  rightMontageName,
  leftPairs,
  rightPairs,
  channels,
  currentTime,
  duration,
  syncPlayback,
  syncCursor,
  syncZoom,
  diffHighlight,
  timeScaleSeconds = 10,
  onSeek,
}) => {
  const uniqueness = useMemo(
    () => buildDualMontageUniquenessMap(leftPairs, rightPairs),
    [leftPairs, rightPairs],
  );

  const leftLanes = useMemo(
    () =>
      deriveDualMontageLanes({
        pairs: leftPairs,
        channels,
        uniquenessByLabel: uniqueness.left,
      }),
    [leftPairs, channels, uniqueness.left],
  );

  const rightLanes = useMemo(
    () =>
      deriveDualMontageLanes({
        pairs: rightPairs,
        channels,
        uniquenessByLabel: uniqueness.right,
      }),
    [rightPairs, channels, uniqueness.right],
  );

  const leftOnlyCount = dualMontagePairLabels(leftPairs).filter((label) =>
    uniqueness.left.get(label) === "left-only",
  ).length;
  const rightOnlyCount = dualMontagePairLabels(rightPairs).filter((label) =>
    uniqueness.right.get(label) === "right-only",
  ).length;

  const effectiveSeek =
    syncPlayback || syncCursor ? onSeek : undefined;
  const sharedScale = syncZoom ? timeScaleSeconds : timeScaleSeconds;

  return (
    <section
      className="dual-montage-review"
      aria-label="Dual-montage synchronized review"
    >
      <div className="dual-montage-review__banner" role="status">
        <span>
          Dual-montage review — Sync playback {syncPlayback ? "on" : "off"},
          cursor {syncCursor ? "locked" : "independent"}, zoom{" "}
          {syncZoom ? "locked" : "independent"}
          {diffHighlight
            ? ` · Diff highlight (L-only ${leftOnlyCount}, R-only ${rightOnlyCount})`
            : ""}
        </span>
      </div>
      <div className="dual-montage-review__panes">
        <DualMontagePane
          title={leftMontageName || "Left montage"}
          lanes={leftLanes}
          currentTime={currentTime}
          duration={duration}
          timeScaleSeconds={sharedScale}
          diffHighlight={diffHighlight}
          emptyMessage={
            leftPairs.length === 0
              ? "Select a left montage in Dual Compare."
              : "No waveform samples loaded for dual review."
          }
          onSeek={effectiveSeek}
        />
        <DualMontagePane
          title={rightMontageName || "Right montage"}
          lanes={rightLanes}
          currentTime={currentTime}
          duration={duration}
          timeScaleSeconds={sharedScale}
          diffHighlight={diffHighlight}
          emptyMessage={
            rightPairs.length === 0
              ? "Select a right montage in Dual Compare."
              : "No waveform samples loaded for dual review."
          }
          onSeek={effectiveSeek}
        />
      </div>
    </section>
  );
};

export default DualMontageReviewPanel;
